import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, Paper,
  TextField, Typography,
} from '@mui/material';
import { InvoiceBrandingService } from '../../services/invoiceBranding.service';
import { AuthService } from '../../services/auth.service';
import type { InvoiceBranding } from '../../interfaces/sales/invoiceBranding.interface';
import { INVOICE_LAYOUTS } from '../../interfaces/sales/invoiceBranding.interface';   // F7
import SearchableSelect from '../common/SearchableSelect';   // F7
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const EMPTY_FORM: InvoiceBranding = {
  institutionId: '',
  displayName: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  tin: '',
  vatRegNo: '',
  areaCode: '',
  bankAccountName: '',
  bankAccountNumber: '',
  bankName: '',
  bankBranch: '',
  bankRoutingNo: '',
  bankSwift: '',
  paymentInstructions: '',
  footerNote: '',
  signatureLeftLabel: '',
  signatureRightLabel: '',
  showSignatures: false,
  showAmountInWords: true,
  hasLogo: false,
  // ---- F7 ----
  layout: 'CLASSIC',
  showDiscountColumn: true,
  showTaxRateColumn: true,
  showHsCodeColumn: false,
  termsText: '',
  banglaLabels: false,
  hasSignatureImage: false,
};

const TERMS_MAX = 2000;   // F7, mirrors terms_text VARCHAR(2000)

const MAX_LOGO_BYTES = 1_048_576; // 1 MB, mirrors the server-side limit

const InvoiceBrandingSettings: React.FC = () => {
  const toast = useToast();
  // Writes are SALES_BRANDING_MANAGE server-side; SALES_BRANDING_VIEW (Accountant) is read-only.
  const canManage = usePermission(PERMISSIONS.SALES_BRANDING_MANAGE);
  const [form, setForm] = useState<InvoiceBranding>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const logoUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // ---- F7 signature image ----
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const signatureUrlRef = useRef<string | null>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    load();
    return () => {
      if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
      if (signatureUrlRef.current) URL.revokeObjectURL(signatureUrlRef.current);   // F7
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      setLoading(true);
      const branding = await InvoiceBrandingService.getByInstitution(institutionId);
      applyBranding(institutionId, branding);
    } catch (e) {
      toast.showApiError(e, 'Failed to load invoice branding');
    } finally {
      setLoading(false);
    }
  };

  const updateLogoUrl = (next: string | null) => {
    setLogoUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return next;
    });
    logoUrlRef.current = next;
  };

  // F7
  const updateSignatureUrl = (next: string | null) => {
    setSignatureUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return next;
    });
    signatureUrlRef.current = next;
  };

  const loadSignature = async (institutionId: string, branding: InvoiceBranding) => {
    if (!branding.hasSignatureImage) {
      updateSignatureUrl(null);
      return;
    }
    try {
      const blob = await InvoiceBrandingService.getSignatureImageBlob(institutionId);
      updateSignatureUrl(URL.createObjectURL(blob));
    } catch {
      updateSignatureUrl(null);
    }
  };

  const applyBranding = async (institutionId: string, branding: InvoiceBranding) => {
    setForm({ ...EMPTY_FORM, ...branding, institutionId });
    loadSignature(institutionId, branding);   // F7
    if (branding.hasLogo) {
      try {
        const blob = await InvoiceBrandingService.getLogoBlob(institutionId);
        updateLogoUrl(URL.createObjectURL(blob));
      } catch {
        updateLogoUrl(null);
      }
    } else {
      updateLogoUrl(null);
    }
  };

  const handleText = (k: keyof InvoiceBranding) =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const handleCheck = (k: 'showSignatures' | 'showAmountInWords' | 'showDiscountColumn' | 'showTaxRateColumn'
    | 'showHsCodeColumn' | 'banglaLabels') =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.checked });

  const handleSave = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      setSaving(true);
      const saved = await InvoiceBrandingService.upsert({ ...form, institutionId });
      setForm({ ...EMPTY_FORM, ...saved, institutionId });
      toast.showSuccess('Invoice branding saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save invoice branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.showApiError(new Error('too large'), 'Logo must be 1 MB or smaller');
      return;
    }
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      const saved = await InvoiceBrandingService.uploadLogo(institutionId, file);
      await applyBranding(institutionId, saved);
      toast.showSuccess('Logo uploaded');
    } catch (err) {
      toast.showApiError(err, 'Failed to upload logo');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      const saved = await InvoiceBrandingService.deleteLogo(institutionId);
      await applyBranding(institutionId, saved);
      toast.showSuccess('Logo removed');
    } catch (err) {
      toast.showApiError(err, 'Failed to remove logo');
    }
  };

  // ---- F7 signature image ----
  const handleSignatureSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.showApiError(new Error('too large'), 'Signature image must be 1 MB or smaller');
      return;
    }
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      const saved = await InvoiceBrandingService.uploadSignatureImage(institutionId, file);
      setForm(prev => ({ ...prev, hasSignatureImage: saved.hasSignatureImage }));
      await loadSignature(institutionId, saved);
      toast.showSuccess('Signature image uploaded');
    } catch (err) {
      toast.showApiError(err, 'Failed to upload the signature image');
    } finally {
      if (signatureInputRef.current) signatureInputRef.current.value = '';
    }
  };

  const handleSignatureDelete = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      const saved = await InvoiceBrandingService.deleteSignatureImage(institutionId);
      setForm(prev => ({ ...prev, hasSignatureImage: saved.hasSignatureImage }));
      updateSignatureUrl(null);
      toast.showSuccess('Signature image removed');
    } catch (err) {
      toast.showApiError(err, 'Failed to remove the signature image');
    }
  };
  // ---- end F7 ----

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const text = (label: string, k: keyof InvoiceBranding, extra: object = {}) => (
    <TextField
      label={label}
      value={(form[k] as string) ?? ''}
      onChange={handleText(k)}
      fullWidth
      disabled={!canManage}
      {...extra}
    />
  );

  return (
    <Box sx={{ p: 3, maxWidth: 720 }} data-help-page="sales.branding.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Invoice Branding</Typography>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="sales.branding.section.main">

        <Typography variant="subtitle1">Logo</Typography>
        {logoUrl && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <img src={logoUrl} alt="Invoice logo" style={{ maxHeight: 64, maxWidth: '100%' }} />
            {canManage && <Button data-help="sales.branding.logoRemove" color="error" onClick={handleLogoDelete}>Remove Logo</Button>}
          </Box>
        )}
        {canManage && <Button data-help="sales.branding.logoUpload" variant="outlined" component="label">
          {logoUrl ? 'Replace Logo' : 'Upload Logo'}
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept="image/png,image/jpeg"
            aria-label="Logo file"
            onChange={handleLogoSelected}
          />
        </Button>}
        <Typography variant="caption" color="text.secondary">
          PNG or JPEG, max 1 MB. Rendered at up to 48pt height on the PDF.
        </Typography>

        <Divider />
        <Typography variant="subtitle1">Header</Typography>
        {text('Display Name', 'displayName', { 'data-help': 'sales.branding.field.displayName' })}
        {text('Address', 'address', { 'data-help': 'sales.branding.field.address' })}
        {text('Phone', 'phone', { 'data-help': 'sales.branding.field.phone' })}
        {text('Email', 'email', { 'data-help': 'sales.branding.field.email' })}
        {text('Website', 'website', { 'data-help': 'sales.branding.field.website' })}

        <Divider />
        <Typography variant="subtitle1">Registration</Typography>
        {text('TIN', 'tin', { 'data-help': 'sales.branding.field.tin' })}
        {text('VAT Reg No', 'vatRegNo', { 'data-help': 'sales.branding.field.vatRegNo' })}
        {text('Area Code', 'areaCode', { 'data-help': 'sales.branding.field.areaCode' })}

        <Divider />
        <Typography variant="subtitle1">Bank &amp; Payment</Typography>
        {text('Bank Account Name', 'bankAccountName', { 'data-help': 'sales.branding.field.bankAccountName' })}
        {text('Bank Account Number', 'bankAccountNumber', { 'data-help': 'sales.branding.field.bankAccountNumber' })}
        {text('Bank Name', 'bankName', { 'data-help': 'sales.branding.field.bankName' })}
        {text('Bank Branch', 'bankBranch', { 'data-help': 'sales.branding.field.bankBranch' })}
        {text('Routing No', 'bankRoutingNo', { 'data-help': 'sales.branding.field.bankRoutingNo' })}
        {text('SWIFT', 'bankSwift', { 'data-help': 'sales.branding.field.bankSwift' })}
        {text('Payment Instructions', 'paymentInstructions', { multiline: true, minRows: 2, 'data-help': 'sales.branding.field.paymentInstructions' })}

        {/* ---- F7 layout ---- */}
        <Divider />
        <Typography variant="subtitle1">Layout</Typography>
        <SearchableSelect
          data-help="sales.branding.field.layout"
          label="Invoice layout"
          options={INVOICE_LAYOUTS}
          value={form.layout ?? 'CLASSIC'}
          getOptionId={l => l.id}
          getOptionLabel={l => l.label}
          onChange={(_id, l) => setForm({ ...form, layout: l ? l.id : 'CLASSIC' })}
          clearable={false}
          disabled={!canManage}
          helperText={INVOICE_LAYOUTS.find(l => l.id === (form.layout ?? 'CLASSIC'))?.hint}
          fullWidth
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2 }}>
          <FormControlLabel data-help="sales.branding.field.discountColumn"
            control={<Checkbox disabled={!canManage} checked={form.showDiscountColumn ?? true} onChange={handleCheck('showDiscountColumn')} />}
            label="Discount columns"
          />
          <FormControlLabel data-help="sales.branding.field.taxRateColumn"
            control={<Checkbox disabled={!canManage} checked={form.showTaxRateColumn ?? true} onChange={handleCheck('showTaxRateColumn')} />}
            label="Tax % column"
          />
          <FormControlLabel data-help="sales.branding.field.hsCodeColumn"
            control={<Checkbox disabled={!canManage} checked={form.showHsCodeColumn ?? false} onChange={handleCheck('showHsCodeColumn')} />}
            label="HS code column"
          />
          <FormControlLabel data-help="sales.branding.field.banglaLabels"
            control={<Checkbox disabled={!canManage} checked={form.banglaLabels ?? false} onChange={handleCheck('banglaLabels')} />}
            label="Bangla labels"
          />
        </Box>
        {text('Terms & Conditions', 'termsText', {
          multiline: true, minRows: 3, inputProps: { maxLength: TERMS_MAX }, 'data-help': 'sales.branding.field.terms',
          helperText: `Printed above the footer note. ${(form.termsText ?? '').length}/${TERMS_MAX}`,
        })}
        {/* ---- end F7 ---- */}

        <Divider />
        <Typography variant="subtitle1">Print Options</Typography>
        <FormControlLabel data-help="sales.branding.field.amountInWords"
          control={<Checkbox disabled={!canManage} checked={form.showAmountInWords} onChange={handleCheck('showAmountInWords')} />}
          label="Show amount in words"
        />
        <FormControlLabel data-help="sales.branding.field.showSignatures"
          control={<Checkbox disabled={!canManage} checked={form.showSignatures} onChange={handleCheck('showSignatures')} />}
          label="Show signature lines"
        />
        {text('Left Signature Label', 'signatureLeftLabel', { placeholder: 'Prepared By', 'data-help': 'sales.branding.field.signatureLeft' })}
        {text('Right Signature Label', 'signatureRightLabel', { placeholder: 'Authorized By', 'data-help': 'sales.branding.field.signatureRight' })}
        {/* ---- F7 signature image (printed over the left signature line when signature lines are on) ---- */}
        <Typography variant="subtitle2">Signature image</Typography>
        {signatureUrl && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <img src={signatureUrl} alt="Invoice signature" style={{ maxHeight: 48, maxWidth: '100%' }} />
            {canManage && <Button data-help="sales.branding.signatureRemove" color="error" onClick={handleSignatureDelete}>Remove Signature</Button>}
          </Box>
        )}
        {canManage && <Button data-help="sales.branding.signatureUpload" variant="outlined" component="label">
          {signatureUrl ? 'Replace Signature' : 'Upload Signature'}
          <input
            ref={signatureInputRef}
            hidden
            type="file"
            accept="image/png,image/jpeg"
            aria-label="Signature file"
            onChange={handleSignatureSelected}
          />
        </Button>}
        <Typography variant="caption" color="text.secondary">
          PNG or JPEG, max 1 MB. Printed over the left signature line when signature lines are shown.
        </Typography>
        {/* ---- end F7 ---- */}
        {text('Footer Note', 'footerNote', { multiline: true, minRows: 2, 'data-help': 'sales.branding.field.footerNote' })}

        {canManage && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button data-help="sales.branding.save" variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default InvoiceBrandingSettings;
